import React, { useState } from "react";
import {
  useCountry,
  useDeleteCountry,
  useEditCountry,
} from "../../hooks/useCountry";
import {
  DeleteCountriesPermission,
  EditCountriesPermission,
} from "../../constants/permissions";
import { usePermission } from "../../hooks/usePermission";
import DataTable from "../shared/DataTable";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import cityService from "../../services/cityService";

function CountriesTable() {
  const { data: countriesData, isLoading } = useCountry();
  const { mutate: deleteCountry } = useDeleteCountry();
  const { mutate: editCountry } = useEditCountry();

  const { data: canEditCountry } = usePermission({
    permissionName: EditCountriesPermission,
  });

  const { data: canDeleteCountry } = usePermission({
    permissionName: DeleteCountriesPermission,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedActionId, setSelectedActionId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: "",
  });
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [relatedCities, setRelatedCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedActionId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedActionId(null);
  };

  const handleEditClick = (country) => {
    setSelectedCountry(country);
    setEditFormData({
      name: country.name,
    });
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleViewClick = async (country) => {
    setSelectedCountry(country);
    setViewDialogOpen(true);
    setCitiesLoading(true);
    handleMenuClose();

    try {
      const cities = await cityService.getCountryCities(country.id);
      setRelatedCities(cities);
    } catch (error) {
      console.error("Error fetching related cities:", error);
      setRelatedCities([]);
    } finally {
      setCitiesLoading(false);
    }
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedCountry(null);
  };

  const handleViewClose = () => {
    setViewDialogOpen(false);
    setSelectedCountry(null);
    setRelatedCities([]);
  };

  const handleEditSubmit = async () => {
    try {
      editCountry({
        id: selectedCountry.id,
        data: editFormData,
      });
      handleEditClose();
    } catch (error) {
      console.error("Error editing country:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this country?")) {
      try {
        deleteCountry(id);
        handleMenuClose();
      } catch (error) {
        console.error("Error deleting country:", error);
      }
    }
  };

  const columns = [
    {
      name: (
        <div style={{ paddingLeft: "300px" }}>
          Name
        </div>
      ),
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div style={{ paddingLeft: "300px" }}>
          {row.name}
        </div>
      ),

    },
    {
      name: "Actions",
      cell: (row) => (
        <IconButton onClick={(e) => handleMenuClick(e, row.id)}>
          <MoreVertIcon />
        </IconButton>
      ),
      width: "400px",
    },
  ];



  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <DataTable
        columns={columns}
        data={countriesData}
        searchFields={["name"]}
        searchPlaceholder="Search countries..."

      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() =>
            handleViewClick(
              countriesData.find((st) => st.id === selectedActionId)
            )
          }
        >
          <i className="fa-solid fa-eye text-blue-600 mr-2 mb-1" />
          <h6 className="text-blueColor">View</h6>
        </MenuItem>
        {canEditCountry && (
          <MenuItem
            onClick={() =>
              handleEditClick(
                countriesData.find((st) => st.id === selectedActionId)
              )
            }
          >
            <i className="fa-solid fa-edit text-green-700 mr-2 mb-1" />
            <h6 className="text-blueColor">Edit</h6>
          </MenuItem>
        )}
        {canDeleteCountry && (
          <MenuItem onClick={() => handleDelete(selectedActionId)}>
            <i className="fa-solid fa-trash text-red-600 mr-2" />
            <h6 className="text-blueColor">Delete</h6>
          </MenuItem>
        )}
      </Menu>

      {/* View Country Modal */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleViewClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="bg-blueColor text-white py-4">
          <i className="fas fa-eye text-xl mx-2"></i>
          <span className="text-xl font-semibold">View Country Details</span>
        </DialogTitle>
        <DialogContent className="mt-4">
          {selectedCountry && (
            <div className="p-4">
              <Box className="mb-6">
                <Typography variant="h6" className="text-gray-800 mb-2">
                  Country Information
                </Typography>
                <Box className="bg-gray-50 p-4 rounded-lg">
                  <Typography variant="body1" className="mb-2">
                    <strong>Name:</strong> {selectedCountry.name}
                  </Typography>
                </Box>
              </Box>

              <Box className="mb-6">
                <h6 className="text-gray-800 mb-2">
                  Related Cities
                </h6>
                {citiesLoading ? (
                  <Box className="flex justify-center p-4">
                    <CircularProgress />
                  </Box>
                ) : relatedCities.length > 0 ? (
                  <Box className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {relatedCities.map((city) => (
                        <div
                          key={city.id}
                          className="bg-blue-100 text-blue-800 p-2 rounded-md text-center"
                        >
                          {city.name}
                        </div>
                      ))}
                    </div>
                    <p className="text-gray-600 mt-4">
                      Total Cities: {relatedCities.length}
                    </p>
                  </Box>
                ) : (
                  <Box className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-500 text-center">
                      No cities found for this country
                    </p>
                  </Box>
                )}
              </Box>
            </div>
          )}
          <div className="flex justify-end gap-4 mt-6 p-4">
            <button
              onClick={handleViewClose}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="bg-blueColor text-white py-4">
          <i className="fas fa-edit text-xl mx-2"></i>
          <span className="text-xl font-semibold">Edit Country</span>
        </DialogTitle>
        <DialogContent className="mt-4">
          <div className="grid grid-cols-1 gap-4 p-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Name
              </label>
              <input
                name="name"
                value={editFormData.name}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, name: e.target.value })
                }
                type="text"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
            </div>
          </div>
          <div className="flex justify-end gap-4 mt-6 p-4">
            <button
              onClick={handleEditClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
            >
              Cancel
            </button>
            <button
              onClick={handleEditSubmit}
              className="px-4 py-2 bg-blueColor text-white rounded"
            >
              Save
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CountriesTable;
